import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

// Define toast type
type ToastType = 'success' | 'danger' | 'warning' | 'info' | 'dark';

// Define toast interface
interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  delay: number;
}

// Define toast context interface
interface ToastContextType {
  showToast: (message: string, type?: ToastType, delay?: number) => void;
  removeToast: (id: number) => void;
}

// Create the context with default values
const ToastContext = createContext<ToastContextType>({
  showToast: () => {},
  removeToast: () => {}
});

/**
 * Custom hook to use the toast context
 * @returns {ToastContextType} Toast context methods and state
 */
export const useToast = (): ToastContextType => useContext(ToastContext);

// Define props interface for ToastProvider
interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Toast provider component
 * Provides toast functionality to the entire application
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  /**
   * Show a toast notification
   * @param {string} message - The message to display
   * @param {ToastType} type - The type of toast (success, danger, warning, info)
   * @param {number} delay - The delay in ms before the toast disappears
   */
  const showToast = (message: string, type: ToastType = 'success', delay: number = 3000): void => {
    const id = Date.now();
    setToasts(prevToasts => [...prevToasts, { id, message, type, delay }]);
  };

  /**
   * Remove a toast notification
   * @param {number} id - The ID of the toast to remove
   */
  const removeToast = (id: number): void => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };

  // Context value
  const value: ToastContextType = {
    showToast,
    removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Toast container */}
      <ToastContainer 
        className="p-3" 
        position="top-end"
        style={{ zIndex: 9999 }}
      >
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            onClose={() => removeToast(toast.id)} 
            bg={toast.type}
            delay={toast.delay}
            autohide
          >
            <Toast.Header closeButton>
              <strong className="me-auto">
                {toast.type === 'success' && 'Success'}
                {toast.type === 'danger' && 'Error'}
                {toast.type === 'warning' && 'Warning'}
                {toast.type === 'info' && 'Information'}
              </strong>
            </Toast.Header>
            <Toast.Body className={toast.type === 'dark' || toast.type === 'danger' ? 'text-white' : ''}>
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

export default ToastContext;
